import { Input } from '@components/ui/input'
import { Slider } from '@components/ui/slider'
import { cn } from '@/utils'
import { COMMON_OFFSET_SLIDER_MAX, COMMON_OFFSET_SLIDER_MIN } from '../constants/wrap-offsets'
import { handleOffsetEnterKey } from '../handlers/offset-enter-key'

interface CommonOffsetSliderProps {
  disabled: boolean
  sliderValue: number[]
  inputText: string
  errorMessage: string
  valuesMixed: boolean
  /** One slider drag tick (live preview only). */
  onSliderDrag: (value: number) => void
  /** Slider drag end / keyboard step — the real commit. */
  onSliderCommit: (value: number) => void
  onInputChange: (raw: string) => void
  onInputFocus: () => void
  onInputBlur: (raw: string) => void
}

/** The shared offset slider plus the "All offsets value" input, its "pt" label and error. */
export function CommonOffsetSlider({
  disabled,
  sliderValue,
  inputText,
  errorMessage,
  valuesMixed,
  onSliderDrag,
  onSliderCommit,
  onInputChange,
  onInputFocus,
  onInputBlur,
}: CommonOffsetSliderProps) {
  return (
    <div className="pt-2">
      <div className="flex items-center gap-2">
        <Slider
          value={sliderValue}
          disabled={disabled}
          onValueChange={(value) => onSliderDrag(value[0] ?? COMMON_OFFSET_SLIDER_MIN)}
          onValueCommit={(value) => onSliderCommit(value[0] ?? COMMON_OFFSET_SLIDER_MIN)}
          min={COMMON_OFFSET_SLIDER_MIN}
          max={COMMON_OFFSET_SLIDER_MAX}
          step={1}
          className="flex-1 min-w-0"
        />
        {/* Input + "pt" label + validation error grouped in their own column so the error
            renders directly below the input (matching Figma), not the full row below the
            slider. */}
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Input
              type="text"
              inputMode="decimal"
              aria-label="All offsets value"
              disabled={disabled}
              value={inputText}
              onFocus={onInputFocus}
              onChange={(e) => onInputChange(e.target.value)}
              onBlur={(e) => onInputBlur(e.target.value)}
              onKeyDown={(e) => handleOffsetEnterKey(e, errorMessage !== '')}
              size="compact"
              error={errorMessage !== ''}
              aria-describedby={errorMessage ? 'common-offset-error' : undefined}
              placeholder={valuesMixed ? 'Mixed' : 'Value'}
              className={cn(
                'h-9 w-24 border-border/80 text-sm',
                inputText !== '' && 'text-base-foreground',
                valuesMixed && inputText === '' && 'placeholder:text-base-foreground'
              )}
            />
            <span className="text-xs font-medium text-muted-foreground leading-none">pt</span>
          </div>
          {errorMessage && (
            <span id="common-offset-error" role="alert" className="text-xs text-destructive">
              {errorMessage}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
